import React from "react";
import {ClusterV1HelmRelease} from "../../../../../../../gen/models/clusterV1HelmRelease";
import {ResultError, Return} from "../../../../../../lib/result";
import {ManagementV1App} from "../../../../../../../gen/models/managementV1App";
import {SectionProps} from "../../../../../../components/Drawer/ItemDrawer";
import Section from "../../../../../../components/Drawer/Section/Section";
import Label from "../../../../../../components/Label/Label";
import Description from "../../../../../../components/Description/Description";
import {HelmRelease} from "../../AppsTable/AppsTable";
import Input from "../../../../../../components/Input/Input";
import {setReleaseMetadata} from "./HelmChartConfig";
import {StorageV1AppParameter} from "../../../../../../../gen/models/storageV1AppParameter";
import SectionExpander from "../../../../../../components/Drawer/SectionExpander/SectionExpander";
import YAMLEditor from "../../../../../../components/YAMLEditor/YAMLEditor";

interface ManifestsState {
    releaseName: string | undefined;
    namespace: string | undefined;
    
    manifests: string;
    values: string | undefined;
    
    parameters: StorageV1AppParameter[] | undefined;
}

interface ManifestsProps extends SectionProps {
    show: boolean;
    release?: HelmRelease;
    namespace?: string;
    selectedApp?: ManagementV1App;
    clusterApp: boolean;
}

export default class Manifests extends React.PureComponent<ManifestsProps, ManifestsState> {
    state: ManifestsState = {
        releaseName: this.props.release?.release.metadata?.name || this.props.selectedApp?.metadata?.name,
        namespace: this.props.namespace || this.props.release?.release.metadata?.namespace || "loft-apps",
        manifests: this.props.release?.release.spec?.manifests || this.props.selectedApp?.spec?.config?.manifests || "",
        
        parameters: this.props.release?.app?.spec?.parameters || this.props.selectedApp?.spec?.parameters,
        values: this.props.release?.release?.spec?.values || this.props.selectedApp?.spec?.config?.values,
    };

    create = (release: ClusterV1HelmRelease): ResultError => {
        if (!this.props.show) {
            return Return.Ok();
        }

        setReleaseMetadata(release, this.state.namespace, this.state.releaseName, this.props.selectedApp, this.props.clusterApp);
        return this.update(release);
    };

    update = (release: ClusterV1HelmRelease): ResultError => {
        if (!this.props.show) {
            return Return.Ok();
        }

        if (!release.spec) {
            release.spec = {};
        }
        release.spec.values = this.state.values;
        release.spec.manifests = this.state.manifests;
        return Return.Ok();
    };

    selectCustomManifests = () => {
        this.setState({
            releaseName: undefined,
            values: undefined,
            manifests: "",
            parameters: undefined,
        });
    }

    selectApp = (app: ManagementV1App | undefined) => {
        if (!app) {
            this.setState({
                values: undefined,
                parameters: undefined,
            });
            
            return;
        }

        this.setState({
            releaseName: app.metadata?.name,
            values: app.spec?.config?.values,
            manifests: app.spec?.config?.manifests || "",
            parameters: app.spec?.parameters,
        });
    }

    renderManifests = () => {
        const valuesInput = <React.Fragment>
            <Label>Kubernetes Manifests</Label>
            <YAMLEditor value={this.state.manifests} 
                        minLines={10}
                        maxLines={100}
                        onChange={val => {
                this.setState({manifests: val});
            }} placeholder={`apiVersion: apps/v1
kind: Deployment
...`} />
            <Description>Each manifest will be created when this app is deployed to a space or virtual cluster and packed as a helm chart. For more information please take a look at the <a href={"https://loft.sh/docs"} target={"_blank"}>Loft documentation</a>.</Description>
        </React.Fragment>
        if (this.props.selectedApp) {
            return <SectionExpander name={"Manifests & Values"}>
                {valuesInput}
                <Label>Optional Values</Label>
                <YAMLEditor value={this.state.values}
                            minLines={10}
                            maxLines={100}
                            onChange={values => this.setState({values: values})} />
                <Description>Optional values used to render the manifests</Description>
            </SectionExpander>
        }

        return valuesInput
    }

    render() {
        if (this.props.mode === "batch" || !this.props.show) {
            return null;
        }

        return <Section title={`Manifests`} noMargin={this.props.mode === "update"}>
                <div className={"row"}>
                    <div style={{flexGrow: 2}}>
                        <Label>Release Name</Label>
                        <Input readOnly={this.props.mode === "update"} value={this.state.releaseName} onChange={e => this.setState({releaseName: e.target.value})} placeholder={"Leave empty for auto-generated release name"} />
                    </div>
                    <div style={{flexGrow: 3}}>
                        <Label>Kubernetes Namespace</Label>
                        <Input readOnly={this.props.mode === "update" || !!this.props.namespace} value={this.state.namespace} onChange={e => this.setState({namespace: e.target.value})} placeholder={"loft-apps"} />
                    </div>
                </div>
            {this.renderManifests()}
        </Section>
    }
}